// ABOUTME: The cell of a `link` additional info field on the link table: a dropdown of the field's nodes on every row.
// ABOUTME: Writes the picked ids into the row's extraInfo entry, which the link table turns into the field value.

import { Autocomplete, Box, styled, Typography } from '@ds/coolshop';
import { SearchFilter, SearchFilterOperator } from '@src/__generated__/types';
import { useCompactLinkSearchQuery } from '@src/components/formFields/compactLink.generated';
import { useGetNodeCodeQuery } from '@src/components/hierarchies/Hierarchy.generated';
import { useMemo, useState } from 'react';

import { encodeLinkInfoIds, LinkInfoField, linkInfoIds } from '../linkInfo';
import { useLinkInfoState } from '../linkInfoState';

type NodeOption = { id: string; code: string };

/** The column's width: the size of the table's own additional info columns. */
export const LINK_INFO_COLUMN_WIDTH = 220;

/**
 * The cell around the input, as the table's own `text` additional info cells lay out theirs
 * (`px={1} py={0.5}` around a small design system input), with the picked codes on one line:
 * however many are picked, neither the row nor the column grows.
 */
const CellInput = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    width: '100%',
    display: 'flex',
    // Zero basis: the input fills the cell but its chips never count towards the column width.
    '& .MuiAutocomplete-root': {
        flex: '1 1 0',
        width: 0,
        minWidth: 0,
    },
    '& .MuiAutocomplete-inputRoot': {
        flexWrap: 'nowrap',
        overflow: 'hidden',
    },
}));

/** How many nodes the dropdown lists: master data (brands, sales orgs) stays well below it. */
const OPTIONS_LIMIT = 500;

type LinkInfoCellProps = {
    field: LinkInfoField;
    /** the hierarchy and id of the row's linked node, for a restricted field */
    rowHierarchy: string;
    rowId: string;
};

/**
 * Like the table's own additional info cells, only a linked row is edited: an unlinked row (or a
 * read-only form) shows its value as text.
 */
export function LinkInfoCell({ field, rowHierarchy, rowId }: LinkInfoCellProps) {
    const { linkedIds, extraInfo, setExtraInfo, disabled } = useLinkInfoState();
    const linked = linkedIds.has(rowId);
    const entry = extraInfo?.find(candidate => candidate.id === rowId);
    const ids = linkInfoIds(entry?.[field.fieldCode]);

    // A restricted field lists the nodes the row's linked node links to, e.g. the sales orgs of
    // the row's distributorship.
    const filter = useMemo<SearchFilter[]>(
        () =>
            field.linkedByRowNode
                ? [
                      {
                          field: 'system-linkedBy',
                          operator: SearchFilterOperator.IsAnyOf,
                          value: [`${rowHierarchy}-${rowId}`],
                      },
                      // Direct links only: without it the search follows transitive links too.
                      {
                          field: 'linkedByDepth',
                          operator: SearchFilterOperator.Equals,
                          value: ['1,1'],
                      },
                  ]
                : [],
        [field.linkedByRowNode, rowHierarchy, rowId],
    );
    const [opened, setOpened] = useState(false);
    // The options are only needed once the dropdown has been opened, and are read again on every
    // opening: the linked nodes change elsewhere (e.g. sales orgs added to a distributorship), and
    // a cached answer would keep offering the old ones until the page is reloaded.
    const {
        data: optionsData,
        loading,
        refetch: refetchOptions,
    } = useCompactLinkSearchQuery({
        variables: { ids: [field.hierarchy], filter, sort: [], first: OPTIONS_LIMIT },
        skip: !opened,
        fetchPolicy: 'cache-and-network',
    });
    const options = useMemo<NodeOption[]>(
        () =>
            (optionsData?.searchHierarchies.nodes.edges ?? [])
                // The hierarchy's root node is not a value.
                .filter(({ node }) => node.id !== field.hierarchy)
                .map(({ node }) => ({ id: node.id, code: node.code })),
        [optionsData, field.hierarchy],
    );

    // A stored id the dropdown no longer lists (e.g. a sales org taken off the distributorship)
    // still shows its code.
    const { data: codesResult, previousData: previousCodes } = useGetNodeCodeQuery({
        variables: { hierarchiesId: field.hierarchy, nodeFilter: { ids } },
        skip: !ids.length,
    });
    const codeById = new Map([
        // The previous answer keeps the codes shown while the ids just picked load.
        ...((codesResult ?? previousCodes)?.hierarchies?.[0]?.nodes ?? []).flatMap(node =>
            node ? [[node.id, node.code] as [string, string]] : [],
        ),
        ...options.map(option => [option.id, option.code] as [string, string]),
    ]);
    const selected = ids.map(id => ({ id, code: codeById.get(id) ?? id }));

    // The same update the table's own additional info cells make: the row's entry, field replaced.
    const write = (picked: string[]) => {
        setExtraInfo?.(current => [
            ...(current ?? []).filter(candidate => candidate.id !== rowId),
            {
                ...entry,
                id: rowId,
                [field.fieldCode]: encodeLinkInfoIds(picked, field.multiValue),
            },
        ]);
    };

    if (!linked || disabled || field.readonly || !setExtraInfo) {
        return (
            <CellInput>
                <Typography variant="body2" noWrap>
                    {selected.map(option => option.code).join(', ')}
                </Typography>
            </CellInput>
        );
    }

    const common = {
        size: 'small' as const,
        onOpen: () => {
            if (opened) {
                refetchOptions();
            } else {
                setOpened(true);
            }
        },
        options,
        loading,
        getOptionLabel: (option: NodeOption) => option.code,
        isOptionEqualToValue: (option: NodeOption, value: NodeOption) => option.id === value.id,
    };

    return (
        // A click in the cell must not tick or untick the row.
        <CellInput onClick={event => event.stopPropagation()}>
            {field.multiValue ? (
                <Autocomplete
                    {...common}
                    multiple
                    disableCloseOnSelect
                    limitTags={2}
                    ChipProps={{ size: 'small' }}
                    value={selected}
                    onChange={(_event, value) => write(value.map(option => option.id))}
                />
            ) : (
                <Autocomplete
                    {...common}
                    value={selected[0] ?? null}
                    onChange={(_event, value) => write(value ? [value.id] : [])}
                />
            )}
        </CellInput>
    );
}
